// requires a loader
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { T, useTolgee, useTranslate } from '@tolgee/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useSession } from 'next-auth/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import PlaySvg from '../assets/icons/PlaySvg';
import classlinkLogo from '../assets/images/LandingPage/classlink_logo.png';
import cleverLogo from '../assets/images/LandingPage/clever_logo.png';
import comalIsd from '../assets/images/LandingPage/comal_isd.png';
import commonSenseMedia from '../assets/images/LandingPage/common_sense_media.svg';
import coppaBadge from '../assets/images/LandingPage/coppa_badge.webp';
import curiVideoCoverImage from '../assets/images/LandingPage/curiVideoCoverImage.png';
import dallasIsd from '../assets/images/LandingPage/dallas_isd_blue.png';
import dalton from '../assets/images/LandingPage/dalton.png';
import ferpaBadge from '../assets/images/LandingPage/ferpa_badge.webp';
import gdprBadge from '../assets/images/LandingPage/gdpr_badge.webp';
import georgiaConnectionsAcademy from '../assets/images/LandingPage/georgia_connections_academy.png';
import googleForEducationLogo from '../assets/images/LandingPage/googleforeducation.webp';
import gwinnettCounty from '../assets/images/LandingPage/gwinnett_county.svg';
import hillsboroughCounty from '../assets/images/LandingPage/hillsborough_county_hcps.png';
import medina from '../assets/images/LandingPage/medina.png';
import memphisShelby from '../assets/images/LandingPage/memphis_shelby_mscs.png';
import mercer from '../assets/images/LandingPage/mercer.png';
import pinecrest from '../assets/images/LandingPage/pinecrest.png';
import psusd from '../assets/images/LandingPage/psusd.png';
import roysecity from '../assets/images/LandingPage/roysecity.png';
import sanDiegoUsd from '../assets/images/LandingPage/san_diego_usd.png';
import texasconnections from '../assets/images/LandingPage/texasconnections.png';
import vanguard from '../assets/images/LandingPage/vanguard.png';
import videoCover from '../assets/images/LandingPage/video-cover.png';
import wakeCounty from '../assets/images/LandingPage/wake_county_wcpss.svg';
import willis from '../assets/images/LandingPage/willis.png';
import theme from '../assets/theme';
import { useBreakPoint } from '../hooks/useBreakPoint';
import { useLocale } from '../hooks/useLocale';
import { useMetrics } from '../hooks/useMetrics';
import { getTrackingParams } from '../utils/adsConversionTrackingUtils';
import { zIndexes } from '../utils/zIndexes';
import { Button } from './_atoms/Button';

const heroBackgroundVideo = '/hero-background-video.mp4';
const CURRICULUM_SERIF = `'Source Serif 4', 'Source Serif Pro', 'Iowan Old Style', Georgia, serif`;

const Container = styled.div`
  width: 100%;
`;

const GetStudentsExcitedSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.landingPageBeige};
  align-items: center;
  justify-content: center;
  height: calc(100dvh - 60px);
  overflow: hidden;
`;

const LandingPageContent = styled.div`
  padding: 100px 24px 50px 24px;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
`;

const baseHeadingStyles = css`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  max-width: 1000px;
  margin-right: auto;
  margin-left: auto;
  font-weight: 900;
  z-index: 1;

  @media (max-width: 600px) {
    font-size: 24px;
  }
`;

const StyledH1 = styled.h1`
  ${baseHeadingStyles}
  margin-bottom: 0;
`;

const StyledH2 = styled.h2`
  ${baseHeadingStyles}
`;

const SectionSubtitle = styled.p`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  max-width: 900px;
  z-index: 1;
  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

/* ─── Shared case-study-style section primitives ──────────────────────────── */

const MOBILE_QUERY = '(max-width: 992px)';

const SectionTitle = styled.h2`
  font-size: 36px;
  color: ${theme.colors.black};
  margin: 0 0 24px;
  line-height: 1.2;

  @media ${MOBILE_QUERY} {
    font-size: 32px;
  }
`;

const CenteredSectionInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

// Animation for the rotating word
const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOutUp = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-20px);
  }
`;

const AnimatedWordContainer = styled.span`
  display: inline;
`;

const AnimatedWord = styled.span<{ $isExiting: boolean }>`
  display: inline;
  font-weight: 900;
  animation: ${({ $isExiting }) => ($isExiting ? fadeOutUp : fadeInUp)} 0.4s ease-out
    forwards;
`;

const HeroSubtitleText = styled.p`
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  z-index: 1;
  margin: 0;
  max-width: 700px;
  color: white;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

const TitleContainerOuter = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const TitleContainerBackground = styled.div`
  position: absolute;
  inset: 0;
  mask-image: linear-gradient(to bottom, transparent, black 20%, black 80%, transparent);
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent,
    black 20%,
    black 80%,
    transparent
  );
  -webkit-mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  pointer-events: none;
`;

const TitleContainerBackgroundInner = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  mask-image: linear-gradient(to right, transparent, black 20%, black 80%, transparent);
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black 20%,
    black 80%,
    transparent
  );
  -webkit-mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
`;

const TitleContainerInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  padding: 100px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const HeroButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  z-index: 1;
  box-sizing: border-box;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
    width: 100%;
    padding: 0 20px;
  }
`;

const HeaderMobileSectionTitle = styled.h1`
  font-size: 30px;
  font-weight: 700;
  margin-bottom: 0;
  text-align: center;
  max-width: 1000px;
  margin-right: auto;
  margin-left: auto;
  font-weight: 900;
  z-index: 1;
`;

const WhiteHeaderMobileSectionTitle = styled(HeaderMobileSectionTitle)`
  color: white;
`;

const WhiteStyledH1 = styled(StyledH1)`
  color: white;
`;

const BackgroundVideoContainer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  overflow: hidden;
`;

const BackgroundVideo = styled.video`
  position: absolute;
  width: 100%;
  object-fit: cover;
  height: 100%;
`;

const BackgroundImage = styled.img<{ $isVisible: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.5s ease;
  pointer-events: none;
`;

const HeroCenterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  max-width: 900px;
  box-sizing: border-box;
`;

const TitleSubtitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
  width: 100%;
`;

const LogoWrapper = styled.div`
  flex-shrink: 0;
  height: 80px;
  width: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  /* padding: 14px 20px; */
  /* background-color: ${theme.colors.white}; */
  /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.04); */
  box-sizing: border-box;

  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }
`;

export const MichiganImpactCaseStudyTitle = (
  <T
    keyName="Michigan Impact Case Study Title"
    defaultValue="Instructional Shifts That Stick (MI)"
  />
);
export const ButlerUniversityCaseStudyTitle = (
  <T
    keyName="Butler University Case Study Title"
    defaultValue="Inclusive Engagement in Higher Ed (Butler University, IN)"
  />
);
export const TexasImpactCaseStudyTitle = (
  <T
    keyName="Texas Impact Case Study Title"
    defaultValue="22% Increase in Meets & Masters on STAAR (TX)"
  />
);
export const TexasImpactCaseStudyTitle2 = (
  <T
    keyName="Texas Impact Case Study Title 2"
    defaultValue="32% Growth in STAAR Scores (TX)"
  />
);
export const TexasImpactCaseStudyTitle3 = (
  <T
    keyName="Texas Impact Case Study Title 3"
    defaultValue="20 Point Growth in Green Valley (TX)"
  />
);
export const CaliforniaImpactCaseStudyTitle = (
  <T keyName="California Impact Case Study Title" defaultValue="46% CAASPP Growth (CA)" />
);
export const InnovationsAcademyCaseStudyTitle = (
  <T
    keyName="Innovations Academy Case Study Title"
    defaultValue="A Virtual School with Double-Digit Growth (CA)"
  />
);
export const SivellsBendCaseStudyTitle = (
  <T
    keyName="Sivells Bend Case Study Title"
    defaultValue="Unprecedented Gains: A Small District's Leap from a C Rating to A in One Year (TX)"
  />
);
export const SouthernCaliforniaImpactCaseStudyTitle = (
  <T
    keyName="Southern California Impact Case Study Title"
    defaultValue="Southern CA – Transformative Writing"
  />
);
export const YsletaMiddleSchoolCaseStudyTitle = (
  <T
    keyName="Ysleta Middle School Case Study Title"
    defaultValue="Accelerating Writing Growth for Emergent Bilingual Students (TX)"
  />
);
export const FlourBluffISDCaseStudyTitle = (
  <T
    keyName="Flour Bluff ISD Case Study Title"
    defaultValue="Small Group Instruction, Big Gains on STAAR (TX)"
  />
);
export const CharlieMarshallCaseStudyTitle = (
  <T
    keyName="Charlie Marshall Case Study Title"
    defaultValue="Five Steps, Real Results: A Principal's Playbook (TX)"
  />
);

type TrustedLogo = { src: string; alt: string };

const TrustedByScroller = () => {
  const logos: TrustedLogo[] = [
    { src: pinecrest.src, alt: 'Pinecrest' },
    { src: vanguard.src, alt: 'Vanguard' },
    { src: willis.src, alt: 'Willis ISD' },
    { src: dalton.src, alt: 'Dalton' },
    { src: psusd.src, alt: 'Palm Springs USD' },
    { src: georgiaConnectionsAcademy.src, alt: 'Georgia Connections Academy' },
    { src: texasconnections.src, alt: 'Texas Connections Academy' },
    { src: medina.src, alt: 'Medina Valley ISD' },
    { src: mercer.src, alt: 'Mercer' },
    { src: roysecity.src, alt: 'Royse City ISD' },
    { src: comalIsd.src, alt: 'Comal ISD' },
    { src: dallasIsd.src, alt: 'Dallas ISD' },
    { src: gwinnettCounty.src, alt: 'Gwinnett County Public Schools' },
    { src: hillsboroughCounty.src, alt: 'Hillsborough County Public Schools' },
    { src: memphisShelby.src, alt: 'Memphis-Shelby County Schools' },
    { src: sanDiegoUsd.src, alt: 'San Diego Unified School District' },
    { src: wakeCounty.src, alt: 'Wake County Public School System' },
  ];

  // Triple the track to create seamless scrolling
  const track = [...logos, ...logos, ...logos];

  return (
    <TrustedByFullBleedSection>
      <TrustedByHeading>
        <T
          keyName="LandingPage.ScrollingLogos.Heading_1"
          defaultValue="Trusted in more than 14,000 districts"
        />
      </TrustedByHeading>
      <TrustedByTrack>
        {track.map((entry, index) => (
          <LogoWrapper key={index}>
            <img src={entry.src} alt={entry.alt} />
          </LogoWrapper>
        ))}
      </TrustedByTrack>
    </TrustedByFullBleedSection>
  );
};

const TrustedByFullBleedSection = styled.section`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 0 20px 0;
  background-color: ${theme.colors.background};
  overflow: hidden;

  @media ${MOBILE_QUERY} {
    padding: 60px 0;
  }
`;

const TrustedByHeading = styled.h2`
  font-size: 20px;
  font-weight: 900;
  color: ${theme.colors.black};
  margin: 0 0 48px 0;
  padding: 0 20px;
  text-align: center;
  line-height: 1.4;

  @media (max-width: 600px) {
    font-size: 16px;
    margin-bottom: 32px;
  }
`;

const TrustedByTrack = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  animation: scroll-trusted-by 40s linear infinite;
  width: max-content;

  &:hover {
    animation-play-state: paused;
  }

  @keyframes scroll-trusted-by {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(calc(-33.333% - 20px));
    }
  }
`;

const VideoSection = () => {
  const metrics = useMetrics(true);
  const isDesktop = useBreakPoint(900);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const { t } = useTranslate();

  return (
    <LandingPageContent id="video-in-action">
      <StyledH2>
        <T
          keyName="LandingPage.VideoSection.Heading"
          defaultValue="See Curipod in action"
        />
      </StyledH2>
      {isPlayingVideo ? (
        <iframe
          style={{
            borderRadius: 10,
            marginTop: 40,
            marginBottom: 20,
            zIndex: 1,
            maxWidth: '900px',
            visibility: isPlayingVideo ? 'visible' : 'hidden',
            border: 0,
          }}
          width="80%"
          height={isDesktop ? '500' : '300'}
          src="https://www.youtube.com/embed/I3jj6RS9XVs?si=8IEfd2Z047R46-dx&autoplay=1"
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      ) : (
        <PlayButtonOverlay>
          <img
            style={{
              borderRadius: 10,
              marginTop: 40,
              marginBottom: 20,
              zIndex: 1,
              width: '100%',
              maxWidth: '900px',
              visibility: isPlayingVideo ? 'hidden' : 'visible',
            }}
            src={videoCover.src}
            alt={t('Video cover', 'Video cover')}
            onClick={() => {
              setIsPlayingVideo(true);
              metrics.logEvent('FrontPage.VideoClicked', {
                from: 'bottom',
              });
            }}
          />
          <PlayButton
            aria-label={t(
              'LandingPage.GetStudentsExcited.PlayVideoAriaLabel',
              'Play the video in full screen',
            )}
            onClick={() => setIsPlayingVideo(true)}
          >
            <PlaySvg />
          </PlayButton>
        </PlayButtonOverlay>
      )}
    </LandingPageContent>
  );
};

// Animated phrases for the hero title.
// "Writing students love" must always be the first entry.
// Texas (en-US-TX) and California (en-US-CA) get hard-coded English phrases; all
// other locales (incl. plain en-US) go through Tolgee.
const ANIMATION_INTERVAL = 2000; // 4 seconds

const ROLLING_PHRASES_TX: string[] = [
  'Writing Practice',
  'STAAR Practice',
  'SCR & ECR Practice',
  'TELPAS Practice',
  // 'TEKS-aligned lessons',
];

const ROLLING_PHRASES_CA: string[] = [
  'Writing Practice',
  'CAASPP Practice',
  'ELPAC lessons',
  'Vocabulary Practice',
];

const useRollingPhrases = (): string[] => {
  const tolgee = useTolgee(['language']);
  const { t } = useTranslate();
  const lang = tolgee.getLanguage();

  if (lang === 'en-US-TX') return ROLLING_PHRASES_TX;
  if (lang === 'en-US-CA') return ROLLING_PHRASES_CA;

  return [
    t('LandingPage.Hero.AnimatedWord.Lessons.v1', 'Writing Practice'),
    t('LandingPage.Hero.AnimatedWord.Practice', 'Practice'),
    t('LandingPage.Hero.AnimatedWord.Activities', 'Activities'),
    t('LandingPage.Hero.AnimatedWord.TestPrep', 'Test prep'),
  ];
};

const AnimatedTitleWord = () => {
  const animatedWords = useRollingPhrases();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    let mounted = true;

    const interval = setInterval(() => {
      setIsExiting(true);
      setTimeout(() => {
        if (!mounted) return;
        setCurrentIndex(prev => (prev + 1) % animatedWords.length);
        setIsExiting(false);
      }, 200); // Match the animation duration
    }, ANIMATION_INTERVAL);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [animatedWords.length]);

  return (
    <AnimatedWordContainer>
      <AnimatedWord $isExiting={isExiting}>{animatedWords[currentIndex]}</AnimatedWord>
    </AnimatedWordContainer>
  );
};

const GetStudentsExcited = ({
  onSignup,
  onBookDemo,
  isDesktop,
}: {
  onSignup: () => void;
  onBookDemo: () => void;
  onProvenOutcomesClick: () => void;
  isDesktop: boolean;
}) => {
  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideoCanPlay = () => {
    setIsVideoLoaded(true);
  };

  const renderTitle = () => (
    <>
      <AnimatedTitleWord />{' '}
      <T keyName="LandingPage.Hero.TitleSuffix" defaultValue="Students Love" />
    </>
  );

  const renderSubtitle = () => (
    <HeroSubtitleText>
      <T
        keyName="LandingPage.Hero.SubtitleAligned.v2"
        defaultValue="Teacher led lessons aligned to your district's curriculum. Students read, write and discuss. Not alone on a screen. Together."
      />
    </HeroSubtitleText>
  );

  const renderButtons = () => (
    <HeroButtonsContainer>
      {!isAuthenticated && (
        <OrangePrimaryButton onClick={onSignup} styles={{ width: '210px' }}>
          <T keyName="LandingPage.Hero.SignUpFree" defaultValue="Sign up - it's free" />
        </OrangePrimaryButton>
      )}
      <WhiteSecondaryButton type="ghost" onClick={onBookDemo} styles={{ width: '210px' }}>
        <T keyName="LandingPage.Hero.BookDemo" defaultValue="Book a demo" />
      </WhiteSecondaryButton>
    </HeroButtonsContainer>
  );

  return (
    <>
      <GetStudentsExcitedSection>
        <BackgroundVideoContainer>
          <BackgroundImage
            src={curiVideoCoverImage.src}
            alt=""
            $isVisible={!isVideoLoaded}
          />
          <BackgroundVideo
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            onCanPlay={handleVideoCanPlay}
          >
            <source src={heroBackgroundVideo} type="video/mp4" />
          </BackgroundVideo>
        </BackgroundVideoContainer>
        <HeroCenterWrapper>
          <TitleContainerOuter>
            <TitleContainerBackground>
              <TitleContainerBackgroundInner />
            </TitleContainerBackground>
            <TitleContainerInner>
              <TitleSubtitleWrapper>
                {isDesktop ? (
                  <WhiteStyledH1>{renderTitle()}</WhiteStyledH1>
                ) : (
                  <WhiteHeaderMobileSectionTitle>
                    {renderTitle()}
                  </WhiteHeaderMobileSectionTitle>
                )}
                {renderSubtitle()}
                {renderButtons()}
              </TitleSubtitleWrapper>
            </TitleContainerInner>
          </TitleContainerOuter>
        </HeroCenterWrapper>
      </GetStudentsExcitedSection>
    </>
  );
};

const PlayButtonOverlay = styled.div`
  position: relative;
  width: 80%;
  height: auto;
  max-width: 900px;
  z-index: ${zIndexes('LANDING_PAGE_VIDEO_PLAY_BUTTON_OVERLAY')};
`;

const PlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 30px;
    width: 30px;
  }
  border-radius: 10px;
  height: 70px;
  width: 70px;
  background-color: ${theme.colors.red};
  color: white;
  cursor: pointer;
  outline: none;
  border: none;

  &:hover {
    border: none;
    outline: none;
    background-color: rgba(0, 0, 0, 0.5);
    scale: 1.05;
  }
`;

const OrangePrimaryButton = styled(Button)`
  background-color: ${theme.colors.orange};
  width: 100%;
  height: 42px;
  border-radius: 5px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  span {
    font-size: 20px;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
    box-shadow: none;
  }
`;

const WhiteSecondaryButton = styled(Button)`
  background-color: ${theme.colors.leastTranslucentWhite};
  color: ${theme.colors.black};
  width: 100%;
  height: 42px;
  border-radius: 5px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  border: 1px solid ${theme.colors.black};

  span {
    font-size: 20px;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.greyLight}99;
    color: ${theme.colors.black};
    border: 1px solid ${theme.colors.black};
    box-shadow: none;
  }
`;

// ============================================================
// ProvenResultsBarChart — animated bars (50% baseline, 73% Curipod)
// CurriculumPuzzleAnimation — interlocking puzzle pieces
// Both retrigger on every viewport entry (scroll up or down).
// ============================================================

const useScrollRetrigger = (
  ref: React.RefObject<HTMLElement>,
  className: string,
  threshold = 0.3,
) => {
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const trigger = () => {
      node.classList.remove(className);
      // Force reflow so the keyframe animation restarts cleanly.
      void node.offsetWidth;
      node.classList.add(className);
    };

    if (typeof IntersectionObserver === 'undefined') {
      trigger();
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trigger();
          } else {
            node.classList.remove(className);
          }
        });
      },
      { threshold },
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [ref, className, threshold]);
};

const growBarKf = keyframes`
  from { transform: scaleY(0); }
  to   { transform: scaleY(1); }
`;

const valueFadeKf = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const labelFadeKf = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ProvenResultsBarChart = () => {
  const cardRef = useRef<HTMLDivElement>(null);
  useScrollRetrigger(cardRef, 'is-running');

  useEffect(() => {
    const node = cardRef.current;
    if (!node) return;
    const update = () => {
      node.style.setProperty('--chart-w', `${node.clientWidth}px`);
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  return (
    <BarChartCard
      ref={cardRef}
      role="img"
      aria-label="Students scored 23 points higher on state tests. Baseline 50 percent, with Curipod 73 percent."
    >
      <BarChartTitle>Students Scored 23 Points Higher on State Tests</BarChartTitle>
      <BarChartArea>
        <BarColumn>
          <BarTrack>
            <BarWrap>
              <BarValue $variant="baseline" $bottom={50}>
                50%
              </BarValue>
              <Bar $variant="baseline" $target={50} />
            </BarWrap>
          </BarTrack>
          <BarLabel $variant="baseline">Baseline</BarLabel>
        </BarColumn>
        <BarColumn>
          <BarTrack>
            <BarWrap>
              <BarValue $variant="curipod" $bottom={73}>
                73%
              </BarValue>
              <Bar $variant="curipod" $target={73} />
            </BarWrap>
          </BarTrack>
          <BarLabel $variant="curipod">With Curipod</BarLabel>
        </BarColumn>
      </BarChartArea>
    </BarChartCard>
  );
};

const BarChartCard = styled.div`
  width: 100%;
  height: 100%;
  background: #f0eee9;
  display: flex;
  flex-direction: column;
  --chart-w: 600px;
  padding: clamp(28px, calc(0.06 * var(--chart-w)), 64px)
    clamp(28px, calc(0.08 * var(--chart-w)), 80px)
    clamp(24px, calc(0.05 * var(--chart-w)), 56px);

  @media ${MOBILE_QUERY} {
    padding: 24px 16px 20px;
  }
`;

const BarChartTitle = styled.h3`
  font-family: 'Nunito', system-ui, sans-serif;
  font-weight: 900;
  color: #1a1530;
  font-size: clamp(14px, calc(0.03 * var(--chart-w, 600px)), 30px);
  letter-spacing: 0.02em;
  text-transform: uppercase;
  line-height: 1.2;
  margin: 0 0 8px;
  text-align: center;
  white-space: nowrap;
  max-width: 100%;

  @media ${MOBILE_QUERY} {
    font-size: clamp(13px, 3.4vw, 18px);
    white-space: normal;
    text-wrap: balance;
    margin-bottom: 12px;
  }
`;

const BarChartArea = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  gap: clamp(32px, calc(0.08 * var(--chart-w, 600px)), 120px);
  justify-items: center;
  align-items: stretch;
  padding: 0 clamp(12px, calc(0.06 * var(--chart-w, 600px)), 80px);
  min-height: 0;

  @media ${MOBILE_QUERY} {
    gap: 16px;
    padding: 0;
  }
`;

const BarColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 100%;
  max-width: 220px;
  min-height: 0;
`;

const BarTrack = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const BarWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 200px;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

type BarVariant = 'baseline' | 'curipod';

const Bar = styled.div<{ $variant: BarVariant; $target: number }>`
  width: 100%;
  max-width: 200px;
  height: ${({ $target }) => $target}%;
  background: ${({ $variant }) => ($variant === 'curipod' ? '#F99746' : '#FFFDF6')};
  border: 3px solid #1a1530;
  transform-origin: bottom center;

  ${BarChartCard}.is-running & {
    animation: ${growBarKf} 1000ms cubic-bezier(0.22, 1, 0.36, 1) 550ms both;
  }

  @media (prefers-reduced-motion: reduce) {
    ${BarChartCard}.is-running & {
      animation-duration: 1ms;
      animation-delay: 0ms;
    }
  }
`;

const BarValue = styled.div<{ $variant: BarVariant; $bottom: number }>`
  position: absolute;
  left: 50%;
  bottom: ${({ $bottom }) => $bottom}%;
  transform: translateX(-50%);
  font-family: 'Fraunces', 'Source Serif 4', 'Source Serif Pro', 'Iowan Old Style',
    Georgia, 'Times New Roman', serif;
  font-weight: 800;
  font-style: italic;
  font-size: clamp(36px, calc(0.09 * var(--chart-w, 600px)), 64px);
  line-height: 1;
  margin-bottom: 10px;
  color: ${({ $variant }) => ($variant === 'curipod' ? '#E87B27' : '#1A1530')};
  opacity: 1;
  letter-spacing: -0.01em;
  white-space: nowrap;
  pointer-events: none;

  @media ${MOBILE_QUERY} {
    font-size: 34px;
    margin-bottom: 8px;
  }

  ${BarChartCard}.is-running & {
    animation: ${valueFadeKf} 450ms ease-out 1590ms both;
  }

  @media (prefers-reduced-motion: reduce) {
    ${BarChartCard}.is-running & {
      animation-duration: 1ms;
      animation-delay: 0ms;
    }
  }
`;

const BarLabel = styled.div<{ $variant: BarVariant }>`
  margin-top: 18px;
  font-family: 'Nunito', system-ui, sans-serif;
  font-weight: 800;
  font-size: clamp(12px, calc(0.024 * var(--chart-w, 600px)), 16px);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ $variant }) => ($variant === 'curipod' ? '#E87B27' : '#1A1530')};
  opacity: 1;
  transform: none;
  text-align: center;

  @media ${MOBILE_QUERY} {
    margin-top: 12px;
    font-size: 11px;
    letter-spacing: 0.14em;
    white-space: nowrap;
  }

  ${BarChartCard}.is-running & {
    animation: ${labelFadeKf} 400ms ease-out 1740ms both;
  }

  @media (prefers-reduced-motion: reduce) {
    ${BarChartCard}.is-running & {
      animation-duration: 1ms;
      animation-delay: 0ms;
    }
  }
`;

// ----- Curriculum / Curipod puzzle ----------------------------

const slideTopKf = keyframes`
  from { transform: translateY(-90px) rotate(-6deg); }
  to   { transform: translateY(0) rotate(0); }
`;

const slideBottomKf = keyframes`
  from { transform: translateY(90px) rotate(6deg); }
  to   { transform: translateY(0) rotate(0); }
`;

const PUZZLE_W = 800;
const PUZZLE_H = 748;

const CurriculumPuzzleAnimation = ({ isTexas = false }: { isTexas?: boolean }) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const piecesRef = useRef<HTMLDivElement>(null);

  // Fit the puzzle inside its container while preserving its 800:748 aspect ratio.
  useEffect(() => {
    const stage = stageRef.current;
    const pieces = piecesRef.current;
    if (!stage || !pieces) return;

    const PAD = 0.94;
    const layout = () => {
      const cw = stage.clientWidth;
      const ch = stage.clientHeight;
      if (!cw || !ch) return;
      const scale = Math.min(cw / PUZZLE_W, ch / PUZZLE_H) * PAD;
      pieces.style.width = `${PUZZLE_W * scale}px`;
      pieces.style.height = `${PUZZLE_H * scale}px`;
    };
    layout();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', layout);
      return () => window.removeEventListener('resize', layout);
    }
    const ro = new ResizeObserver(layout);
    ro.observe(stage);
    return () => ro.disconnect();
  }, []);

  useScrollRetrigger(piecesRef, 'go');

  return (
    <PuzzleStage ref={stageRef}>
      <PuzzlePieces ref={piecesRef}>
        <PuzzleTopPanel
          viewBox="0 0 800 420"
          preserveAspectRatio="xMidYMid meet"
          aria-label="Your curriculum: Scope and sequence"
        >
          <path
            d="M 28 2 L 772 2 Q 798 2 798 28 L 798 374 L 446 374 A 46 46 0 0 0 354 374 L 2 374 L 2 28 Q 2 2 28 2 Z"
            fill="#ffffff"
            stroke="#1f2541"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          <text
            x="400"
            y="92"
            textAnchor="middle"
            className="eyebrow"
            fontSize="20"
            fill="#1f2541"
          >
            YOUR CURRICULUM
          </text>
          <text
            x="400"
            y="208"
            textAnchor="middle"
            className="heading"
            fontSize="76"
            fill="#1f2541"
          >
            Scope &amp; sequence
          </text>
          <text
            x="400"
            y="278"
            textAnchor="middle"
            className="subtitle"
            fontSize="32"
            fill="#3a4067"
          >
            {isTexas
              ? 'HMH · CKLA · Bluebonnet Learning · Wonders'
              : 'HMH · CKLA · Eureka · Wonders'}
          </text>
        </PuzzleTopPanel>
        <PuzzleBottomPanel
          viewBox="0 0 800 440"
          preserveAspectRatio="xMidYMid meet"
          aria-label="Curipod: Writing and feedback"
        >
          <path
            d="M 2 66 L 354 66 A 46 46 0 0 1 446 66 L 798 66 L 798 412 Q 798 438 772 438 L 28 438 Q 2 438 2 412 Z"
            fill="#fbdcc3"
            stroke="#f99746"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          <text
            x="400"
            y="120"
            textAnchor="middle"
            className="eyebrow"
            fontSize="20"
            fill="#f99746"
          >
            CURIPOD
          </text>
          <text
            x="400"
            y="244"
            textAnchor="middle"
            className="heading"
            fontSize="76"
            fill="#1f2541"
          >
            Writing &amp; feedback
          </text>
          <text
            x="400"
            y="316"
            textAnchor="middle"
            className="subtitle"
            fontSize="32"
            fill="#3a4067"
          >
            Engagement · Practice · AI feedback
          </text>
        </PuzzleBottomPanel>
      </PuzzlePieces>
    </PuzzleStage>
  );
};

const PuzzleStage = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(12px, 4%, 40px);
`;

const PuzzlePieces = styled.div`
  position: relative;
  /* width and height set by JS to preserve the 800:748 aspect ratio. */
  width: 0;
  height: 0;
`;

const puzzleTextStyles = css`
  .eyebrow {
    font-family: 'Nunito', system-ui, sans-serif;
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
  }
  .heading {
    font-family: 'Source Serif 4', 'Source Serif Pro', Georgia, serif;
    font-weight: 500;
    letter-spacing: -0.01em;
  }
  .subtitle {
    font-family: 'Source Serif 4', 'Source Serif Pro', Georgia, serif;
    font-style: italic;
    font-weight: 400;
  }
`;

const PuzzleTopPanel = styled.svg`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  /* 420 / 748 of the puzzle box. */
  height: 56.1497%;
  display: block;
  will-change: transform;
  transform-origin: 50% 50%;
  transform: translateY(0) rotate(0);

  ${PuzzlePieces}.go & {
    animation: ${slideTopKf} 900ms cubic-bezier(0.4, 0, 0.2, 1) 450ms both;
  }

  ${puzzleTextStyles}

  @media (prefers-reduced-motion: reduce) {
    ${PuzzlePieces}.go & {
      animation-duration: 1ms;
      animation-delay: 0ms;
    }
  }
`;

const PuzzleBottomPanel = styled.svg`
  position: absolute;
  left: 0;
  /* 308 / 748 of the puzzle box. */
  top: 41.1765%;
  width: 100%;
  /* 440 / 748 of the puzzle box. */
  height: 58.8235%;
  display: block;
  will-change: transform;
  transform-origin: 50% 50%;
  transform: translateY(0) rotate(0);

  ${PuzzlePieces}.go & {
    animation: ${slideBottomKf} 900ms cubic-bezier(0.4, 0, 0.2, 1) 450ms both;
  }

  ${puzzleTextStyles}

  @media (prefers-reduced-motion: reduce) {
    ${PuzzlePieces}.go & {
      animation-duration: 1ms;
      animation-delay: 0ms;
    }
  }
`;

// ============================================================
// CoreValuePropositionSection — 4 horizontal cards
// ============================================================

const CoreValuePropositionSection = ({ onBookDemo }: { onBookDemo: () => void }) => {
  const tolgee = useTolgee(['language']);
  const isTexas = tolgee.getLanguage() === 'en-US-TX';
  const isCalifornia = tolgee.getLanguage() === 'en-US-CA';
  return (
    <ValuePropSection>
      <LandingPageContent>
        <ValuePropRows>
          <ValuePropRow $variant="dark">
            <ValuePropVisual $variant="dark">
              <ProvenResultsBarChart />
            </ValuePropVisual>
            <ValuePropCopy>
              <ValuePropTitle>
                <T
                  keyName="LandingPage.ValueProp.ProvenResults.TitleV2"
                  defaultValue="Real results on state tests"
                />
              </ValuePropTitle>
              <ValuePropBody>
                <T
                  keyName="LandingPage.ValueProp.ProvenResults.BodyV2"
                  defaultValue="Students in Curipod classrooms improved up to 23 percentage points on state reading and writing tests, with the furthest-behind students making the biggest gains."
                />
              </ValuePropBody>
              <ValuePropSource>
                {isTexas ? (
                  <T
                    keyName="LandingPage.ValueProp.ProvenResults.SourceTx"
                    defaultValue="Case studies based on official state test data from the Texas Education Agency."
                  />
                ) : isCalifornia ? (
                  <T
                    keyName="LandingPage.ValueProp.ProvenResults.SourceCa"
                    defaultValue="Case studies based on official state test data from the California Department of Education."
                  />
                ) : (
                  <T
                    keyName="LandingPage.ValueProp.ProvenResults.Source"
                    defaultValue="Case studies based on official state test data from the California Department of Education and the Texas Education Agency."
                  />
                )}
              </ValuePropSource>
              <Link href="/case-studies" passHref>
                <ValuePropLink>
                  <T
                    keyName="LandingPage.ValueProp.ProvenResults.Link"
                    defaultValue="See the studies →"
                  />
                </ValuePropLink>
              </Link>
            </ValuePropCopy>
          </ValuePropRow>

          <ValuePropRow $variant="peach">
            <ValuePropVisual $variant="peach">
              <CurriculumPuzzleAnimation isTexas={isTexas} />
            </ValuePropVisual>
            <ValuePropCopy>
              <ValuePropTitle>
                <T
                  keyName="LandingPage.ValueProp.HQIMAligned.Title"
                  defaultValue="Fits your scope & sequence"
                />
              </ValuePropTitle>
              <ValuePropBody>
                {isTexas ? (
                  <T
                    keyName="LandingPage.ValueProp.HQIMAligned.BodyTx"
                    defaultValue="Use Curipod for Tier 1 and Tier 2 instruction alongside Bluebonnet Learning, HMH Into Reading, Amplify CKLA, Eureka, Wonders, and the other HQIM your district already runs."
                  />
                ) : (
                  <T
                    keyName="LandingPage.ValueProp.HQIMAligned.Body"
                    defaultValue="Use Curipod for Tier 1 and Tier 2 instruction alongside HMH Into Reading, Amplify CKLA, Eureka, Wonders, and the other HQIM your district already runs."
                  />
                )}
              </ValuePropBody>
              <ValuePropLink as="a" href="#hqim-alignment">
                <T
                  keyName="LandingPage.ValueProp.HQIMAligned.Link"
                  defaultValue="See supported curricula →"
                />
              </ValuePropLink>
            </ValuePropCopy>
          </ValuePropRow>

          <ValuePropRow $variant="yellow">
            <ValuePropVisual $variant="yellow">
              <WritingLoopStagger />
            </ValuePropVisual>
            <ValuePropCopy>
              <ValuePropTitle>
                <T
                  keyName="LandingPage.ValueProp.AIFeedback.Title.V2"
                  defaultValue="Specific, and actionable real-time AI feedback"
                />
              </ValuePropTitle>
              <ValuePropBody>
                <T
                  keyName="LandingPage.ValueProp.AIFeedback.BodyV3"
                  defaultValue="Students get feedback the moment they submit. They read it. They discuss it. They try again. All in the same class period, and aligned with Hattie’s research."
                />
              </ValuePropBody>
              <ValuePropLink as="button" type="button" onClick={onBookDemo}>
                <T
                  keyName="LandingPage.ValueProp.AIFeedback.LinkBookDemo"
                  defaultValue="Book a demo →"
                />
              </ValuePropLink>
            </ValuePropCopy>
          </ValuePropRow>

          <ValuePropRow $variant="cream">
            <ValuePropVisual $variant="cream">
              <ReportingInsightCard aria-label="Top misconception across the class">
                <ReportingInsightLabel>
                  <T
                    keyName="LandingPage.ValueProp.Reporting.InsightLabel"
                    defaultValue="Top misconception"
                  />
                </ReportingInsightLabel>
                <ReportingInsightQuote>
                  <T
                    keyName="LandingPage.ValueProp.Reporting.InsightQuoteLead"
                    defaultValue="Students are "
                  />
                  <em>
                    <T
                      keyName="LandingPage.ValueProp.Reporting.InsightQuoteEm"
                      defaultValue="finding supporting evidence"
                    />
                  </em>
                  <T
                    keyName="LandingPage.ValueProp.Reporting.InsightQuoteTail"
                    defaultValue=" in the text, but quoting summary lines instead of specific details."
                  />
                </ReportingInsightQuote>
                <ReportingInsightDivider />
                <ReportingNeedsRow>
                  <ReportingNeedsLeft>
                    <ReportingNeedsCount>3</ReportingNeedsCount>
                    <ReportingNeedsText>
                      <span className="lbl">
                        <T
                          keyName="LandingPage.ValueProp.Reporting.NeedsLabel"
                          defaultValue="Students need help"
                        />
                      </span>
                      <span className="sub">
                        <T
                          keyName="LandingPage.ValueProp.Reporting.NeedsSub"
                          defaultValue="flagged for follow-up"
                        />
                      </span>
                    </ReportingNeedsText>
                  </ReportingNeedsLeft>
                  <ReportingAvatars aria-hidden="true">
                    <span className="av a1">MR</span>
                    <span className="av a2">JT</span>
                    <span className="av a3">SK</span>
                  </ReportingAvatars>
                </ReportingNeedsRow>
              </ReportingInsightCard>
            </ValuePropVisual>
            <ValuePropCopy>
              <ReportingHeadline>
                <T
                  keyName="LandingPage.ValueProp.Reporting.HeadlineLead.v2"
                  defaultValue="Reports and insights from every lesson"
                />
              </ReportingHeadline>
              <ReportingLede>
                <T
                  keyName="LandingPage.ValueProp.Reporting.LedeLead.v2"
                  defaultValue="Curipod turns classroom activity into reports you can act on. Every response, reflection, and revision becomes evidence so you can spot learning gaps, adjust, and differentiate instruction."
                />
              </ReportingLede>
            </ValuePropCopy>
          </ValuePropRow>

          <ValuePropRow $variant="paper">
            <ValuePropVisual $variant="paper">
              <WordCloudVisual />
            </ValuePropVisual>
            <ValuePropCopy>
              <ValuePropTitle>
                <T
                  keyName="LandingPage.ValueProp.Participation.Title"
                  defaultValue="Every voice heard"
                />
              </ValuePropTitle>
              <ValuePropBody>
                <T
                  keyName="LandingPage.ValueProp.Participation.Body"
                  defaultValue="Learning is social. Always has been. Curipod is built around that. Teacher-led, whole-class lessons where every student reads, writes, and discusses — together. Not heads down, alone on a screen. Out loud, in conversation, with you as the facilitator"
                />
              </ValuePropBody>
              <ValuePropLink as="a" href="#video-in-action">
                <T
                  keyName="LandingPage.ValueProp.Participation.Link"
                  defaultValue="See a lesson in action →"
                />
              </ValuePropLink>
            </ValuePropCopy>
          </ValuePropRow>
        </ValuePropRows>
      </LandingPageContent>
    </ValuePropSection>
  );
};

const ValuePropSection = styled.section`
  width: 100%;
  background: ${theme.colors.background};
  padding: 0px 0 100px 0;
  display: flex;
  justify-content: center;

  @media ${MOBILE_QUERY} {
    padding: 60px 0;
  }
`;

type ValuePropVariant = 'dark' | 'peach' | 'yellow' | 'orange' | 'cream' | 'paper';

const valuePropBackground = (variant: ValuePropVariant) => {
  switch (variant) {
    case 'dark':
      return theme.colors.black;
    case 'peach':
      return '#FBD5BB';
    case 'yellow':
      return '#C8A8C4';
    case 'orange':
      return theme.colors.orange;
    case 'cream':
      return '#F3EBDA';
    case 'paper':
      return '#F4EFE6';
  }
};

const ValuePropRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 120px;
  width: 100%;
  margin-top: 80px;

  @media ${MOBILE_QUERY} {
    gap: 64px;
    margin-top: 48px;
  }
`;

const ValuePropRow = styled.div<{ $variant: ValuePropVariant }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;

  &:nth-child(even) > *:first-child {
    order: 2;
  }

  @media ${MOBILE_QUERY} {
    grid-template-columns: 1fr;
    gap: 32px;

    &:nth-child(odd) > *:first-child,
    &:nth-child(even) > *:first-child {
      order: 2;
    }
    &:nth-child(odd) > *:last-child,
    &:nth-child(even) > *:last-child {
      order: 1;
    }
  }
`;

const ValuePropVisual = styled.div<{ $variant: ValuePropVariant }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $variant }) => valuePropBackground($variant)};
  border-radius: 40px;
  aspect-ratio: 5 / 4;
  overflow: hidden;

  @media ${MOBILE_QUERY} {
    border-radius: 32px;
    aspect-ratio: 4 / 3;
  }
`;

// ============================================================
// WritingLoopStagger — Stagger variation of the Write → AI feedback → Reflect loop.
// One-shot animation: hairline rules draw, three numbered nodes rise from below,
// arrows wipe in left-to-right, and a loop arc draws back from step 3 to step 1.
// Triggered when scrolled into view; does not loop.
// ============================================================

const STAGGER_INK = '#1A1530';

const stepRiseKf = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const lineDrawKf = keyframes`
  from { stroke-dashoffset: 1; }
  to   { stroke-dashoffset: 0; }
`;

const fadeInKf = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const StaggerSvg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
  font-family: 'Spectral', 'Source Serif Pro', 'Iowan Old Style', Georgia, serif;

  .stagger-step {
    opacity: 0;
    transform: translateY(24px);
  }
  &.is-running .stagger-step--1 {
    animation: ${stepRiseKf} 700ms cubic-bezier(0.33, 1, 0.68, 1) 200ms both;
  }
  &.is-running .stagger-step--2 {
    animation: ${stepRiseKf} 700ms cubic-bezier(0.33, 1, 0.68, 1) 700ms both;
  }
  &.is-running .stagger-step--3 {
    animation: ${stepRiseKf} 700ms cubic-bezier(0.33, 1, 0.68, 1) 1200ms both;
  }

  .stagger-arrow-line {
    stroke-dashoffset: 1;
  }
  &.is-running .stagger-arrow-line--1 {
    animation: ${lineDrawKf} 450ms cubic-bezier(0.65, 0, 0.35, 1) 550ms both;
  }
  &.is-running .stagger-arrow-line--2 {
    animation: ${lineDrawKf} 450ms cubic-bezier(0.65, 0, 0.35, 1) 1050ms both;
  }

  .stagger-arrow-head {
    opacity: 0;
  }
  &.is-running .stagger-arrow-head--1 {
    animation: ${fadeInKf} 90ms linear 940ms both;
  }
  &.is-running .stagger-arrow-head--2 {
    animation: ${fadeInKf} 90ms linear 1440ms both;
  }

  .stagger-loop {
    stroke-dashoffset: 1;
  }
  &.is-running .stagger-loop {
    animation: ${lineDrawKf} 1000ms cubic-bezier(0.65, 0, 0.35, 1) 1850ms both;
  }

  .stagger-loop-head {
    opacity: 0;
  }
  &.is-running .stagger-loop-head {
    animation: ${fadeInKf} 90ms linear 2800ms both;
  }
`;

const WritingLoopStagger = () => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  useScrollRetrigger((svgRef as unknown) as React.RefObject<HTMLElement>, 'is-running');

  // Geometry — matches scenes.jsx. ViewBox height set to 2 × NY so the node
  // centers (y = NY) land exactly at the viewBox middle; xMidYMid meet then
  // centers the diagram inside the parent ValuePropVisual.
  const W = 1000;
  const NY = 322.5;
  const H = NY * 2; // 645
  const R = 60;
  const N = [
    { x: 175, y: NY },
    { x: 500, y: NY },
    { x: 825, y: NY },
  ];
  const labels = ['Write', ['Immediate', 'AI feedback'], 'Reflect & revise'];

  // Arrow segments stop short of node edges (and leave 8px for the arrowhead).
  const arrows = [
    { x1: N[0].x + R, y1: NY, x2: N[1].x - R - 8, y2: NY },
    { x1: N[1].x + R, y1: NY, x2: N[2].x - R - 8, y2: NY },
  ];

  // Loop arc — top of N3 arcs OVER the figure to top of N1, arriving vertical.
  const arcStart = { x: N[2].x, y: NY - R };
  const arcEnd = { x: N[0].x, y: NY - R };
  const arcHeight = 70; // y at the apex of the arc (small y = high in viewBox)
  const loopPath = `M ${arcStart.x},${arcStart.y} C ${arcStart.x},${arcHeight} ${arcEnd.x},${arcHeight} ${arcEnd.x},${arcEnd.y}`;

  return (
    <StaggerSvg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="The writing cycle: write, immediate AI feedback, reflect and revise"
    >
      {/* Arrow lines (under nodes) */}
      {arrows.map((a, i) => (
        <line
          key={`a${i}`}
          className={`stagger-arrow-line stagger-arrow-line--${i + 1}`}
          x1={a.x1}
          y1={a.y1}
          x2={a.x2}
          y2={a.y2}
          stroke={STAGGER_INK}
          strokeWidth={2.5}
          strokeLinecap="round"
          pathLength={1}
          strokeDasharray="1 1"
        />
      ))}
      {/* Arrow heads — small triangles pointing right at end of each line */}
      {arrows.map((a, i) => (
        <polygon
          key={`ah${i}`}
          className={`stagger-arrow-head stagger-arrow-head--${i + 1}`}
          points={`${a.x2 + 10},${a.y2} ${a.x2},${a.y2 - 5.5} ${a.x2},${a.y2 + 5.5}`}
          fill={STAGGER_INK}
        />
      ))}

      {/* Loop arc */}
      <path
        className="stagger-loop"
        d={loopPath}
        fill="none"
        stroke={STAGGER_INK}
        strokeWidth={2}
        opacity={0.85}
        pathLength={1}
        strokeDasharray="1 1"
      />
      {/* Loop arrowhead — triangle pointing down into top of N1 */}
      <polygon
        className="stagger-loop-head"
        points={`${arcEnd.x},${arcEnd.y} ${arcEnd.x - 6},${arcEnd.y - 10} ${arcEnd.x +
          6},${arcEnd.y - 10}`}
        fill={STAGGER_INK}
      />

      {/* Three steps — each a group that rises into place */}
      {N.map((node, i) => {
        const lbl = labels[i];
        const lines = Array.isArray(lbl) ? lbl : [lbl];
        return (
          <g key={`s${i}`} className={`stagger-step stagger-step--${i + 1}`}>
            <circle
              cx={node.x}
              cy={node.y}
              r={R}
              fill="#FFFFFF"
              stroke={STAGGER_INK}
              strokeWidth={2.5}
            />
            <text
              x={node.x}
              y={node.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={38}
              fontWeight={600}
              fill={STAGGER_INK}
            >
              {i + 1}
            </text>
            {lines.map((line, li) => (
              <text
                key={li}
                x={node.x}
                y={node.y + R + 30 + li * 33.6}
                textAnchor="middle"
                dominantBaseline="hanging"
                fontSize={32}
                fontWeight={400}
                fill={STAGGER_INK}
              >
                {line}
              </text>
            ))}
          </g>
        );
      })}
    </StaggerSvg>
  );
};

// ============================================================
// WordCloudVisual — "the vocabulary of thinking"
// SVG-based so it scales cleanly inside ValuePropVisual.
// ============================================================
type WordCloudEntry = {
  text: string;
  x: number; // percent 0..100
  y: number; // percent 0..100
  size: number; // viewBox units
  family: 'serif' | 'sans' | 'hand';
  weight?: number;
  italic?: boolean;
  color?: 'ink' | 'soft' | 'ghost' | 'accent' | 'accentDeep';
  rot?: number;
  opacity?: number;
};

const WC_FAMILIES: Record<WordCloudEntry['family'], string> = {
  serif: `'Source Serif 4', 'Iowan Old Style', Georgia, serif`,
  sans: `'Nunito', system-ui, sans-serif`,
  hand: `'Caveat', 'Bradley Hand', 'Snell Roundhand', cursive`,
};

const WC_COLORS: Record<NonNullable<WordCloudEntry['color']>, string> = {
  ink: '#14110F',
  soft: '#3A332D',
  ghost: '#C9C0B0',
  accent: '#F07A43',
  accentDeep: '#D85F28',
};

const WORD_CLOUD: WordCloudEntry[] = [
  // Hero anchors
  {
    text: 'because',
    x: 32,
    y: 48,
    size: 92,
    family: 'serif',
    weight: 500,
    italic: true,
    rot: -4,
    color: 'ink',
  },
  {
    text: 'I think',
    x: 62,
    y: 32,
    size: 76,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: 3,
    color: 'accent',
  },
  {
    text: 'why?',
    x: 74,
    y: 62,
    size: 68,
    family: 'serif',
    weight: 700,
    rot: -6,
    color: 'ink',
  },
  {
    text: 'maybe',
    x: 14,
    y: 70,
    size: 60,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: 8,
    color: 'ink',
  },
  // Secondary
  {
    text: 'yes',
    x: 46,
    y: 18,
    size: 52,
    family: 'serif',
    weight: 900,
    rot: -3,
    color: 'ink',
  },
  {
    text: 'disagree',
    x: 50,
    y: 78,
    size: 46,
    family: 'serif',
    weight: 500,
    italic: true,
    rot: 5,
    color: 'ink',
  },
  {
    text: 'and',
    x: 8,
    y: 42,
    size: 42,
    family: 'serif',
    weight: 300,
    italic: true,
    rot: -10,
    color: 'soft',
  },
  {
    text: 'but',
    x: 82,
    y: 44,
    size: 44,
    family: 'serif',
    weight: 500,
    rot: 7,
    color: 'ink',
  },
  {
    text: 'so',
    x: 30,
    y: 86,
    size: 46,
    family: 'serif',
    weight: 700,
    rot: -2,
    color: 'accent',
  },
  {
    text: 'how?',
    x: 66,
    y: 10,
    size: 36,
    family: 'serif',
    weight: 700,
    rot: 4,
    color: 'ink',
  },
  {
    text: 'what if',
    x: 86,
    y: 16,
    size: 30,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: -8,
    color: 'ink',
  },
  {
    text: 'agreed',
    x: 4,
    y: 18,
    size: 30,
    family: 'serif',
    weight: 500,
    rot: 6,
    color: 'ink',
  },
  // Mid-tier
  {
    text: 'actually',
    x: 20,
    y: 30,
    size: 26,
    family: 'serif',
    weight: 500,
    italic: true,
    rot: 5,
    color: 'ink',
  },
  {
    text: 'evidence',
    x: 60,
    y: 52,
    size: 24,
    family: 'sans',
    weight: 600,
    rot: -4,
    color: 'ink',
  },
  {
    text: 'since',
    x: 72,
    y: 24,
    size: 22,
    family: 'serif',
    weight: 400,
    rot: 10,
    color: 'soft',
  },
  {
    text: 'however',
    x: 10,
    y: 54,
    size: 22,
    family: 'serif',
    weight: 500,
    italic: true,
    rot: -5,
    color: 'ink',
  },
  {
    text: 'therefore',
    x: 42,
    y: 64,
    size: 24,
    family: 'serif',
    weight: 500,
    italic: true,
    rot: 2,
    color: 'ink',
  },
  {
    text: 'true',
    x: 22,
    y: 10,
    size: 28,
    family: 'serif',
    weight: 700,
    rot: -4,
    color: 'ink',
  },
  {
    text: 'honestly',
    x: 64,
    y: 76,
    size: 22,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: -7,
    color: 'ink',
  },
  {
    text: 'if',
    x: 52,
    y: 40,
    size: 38,
    family: 'serif',
    weight: 300,
    italic: true,
    rot: 9,
    color: 'soft',
  },
  {
    text: 'then',
    x: 60,
    y: 44,
    size: 24,
    family: 'serif',
    weight: 700,
    rot: -3,
    color: 'ink',
  },
  {
    text: 'wait',
    x: 38,
    y: 22,
    size: 30,
    family: 'hand',
    weight: 700,
    rot: 6,
    color: 'accent',
  },
  {
    text: 'hmm',
    x: 86,
    y: 34,
    size: 40,
    family: 'hand',
    weight: 500,
    rot: -4,
    color: 'ink',
  },
  {
    text: 'oh!',
    x: 26,
    y: 58,
    size: 38,
    family: 'hand',
    weight: 700,
    rot: 12,
    color: 'accent',
  },
  {
    text: 'aha',
    x: 92,
    y: 48,
    size: 32,
    family: 'hand',
    weight: 500,
    rot: -8,
    color: 'ink',
  },
  // Small utility
  {
    text: 'in my view',
    x: 22,
    y: 82,
    size: 16,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: -3,
    color: 'soft',
  },
  {
    text: 'I’d say',
    x: 58,
    y: 22,
    size: 16,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: -9,
    color: 'soft',
  },
  {
    text: 'probably',
    x: 82,
    y: 72,
    size: 16,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: 4,
    color: 'soft',
  },
  {
    text: 'like Maya said',
    x: 10,
    y: 90,
    size: 16,
    family: 'hand',
    weight: 500,
    rot: 6,
    color: 'accentDeep',
  },
  {
    text: 'yes and',
    x: 52,
    y: 58,
    size: 16,
    family: 'sans',
    weight: 600,
    rot: 3,
    color: 'accent',
  },
  {
    text: 'I noticed',
    x: 70,
    y: 40,
    size: 14,
    family: 'sans',
    weight: 500,
    rot: 7,
    color: 'soft',
  },
  {
    text: 'could we',
    x: 46,
    y: 30,
    size: 16,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: -6,
    color: 'soft',
  },
  {
    text: 'is it?',
    x: 70,
    y: 82,
    size: 18,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: 8,
    color: 'ink',
  },
  {
    text: 'same',
    x: 6,
    y: 62,
    size: 16,
    family: 'hand',
    weight: 500,
    rot: 10,
    color: 'accentDeep',
  },
  {
    text: 'huh?',
    x: 38,
    y: 54,
    size: 18,
    family: 'hand',
    weight: 700,
    rot: -12,
    color: 'ink',
  },
  {
    text: '100%',
    x: 18,
    y: 48,
    size: 18,
    family: 'sans',
    weight: 700,
    rot: -4,
    color: 'accentDeep',
  },
  {
    text: 'nope',
    x: 54,
    y: 88,
    size: 16,
    family: 'hand',
    weight: 500,
    rot: 5,
    color: 'soft',
  },
  {
    text: 'I feel',
    x: 30,
    y: 16,
    size: 22,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: 9,
    color: 'ink',
  },
  {
    text: 'because',
    x: 92,
    y: 94,
    size: 12,
    family: 'serif',
    weight: 400,
    italic: true,
    rot: -6,
    color: 'ghost',
    opacity: 0.7,
  },
  {
    text: 'why',
    x: 50,
    y: 6,
    size: 12,
    family: 'serif',
    weight: 500,
    rot: -3,
    color: 'ghost',
    opacity: 0.7,
  },
  {
    text: 'how',
    x: 46,
    y: 94,
    size: 12,
    family: 'serif',
    weight: 500,
    rot: 4,
    color: 'ghost',
    opacity: 0.7,
  },
];

const WC_VIEW_W = 1000;
const WC_VIEW_H = 800;

const WordCloudSvg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;

  text {
    user-select: none;
    animation: wcSettle 0.9s cubic-bezier(0.22, 0.9, 0.35, 1.05) both;
  }

  @keyframes wcSettle {
    0% {
      opacity: 0;
      filter: blur(3px);
    }
    70% {
      opacity: 1;
      filter: blur(0);
    }
    100% {
      opacity: inherit;
      filter: blur(0);
    }
  }
`;

const WordCloudVisual = () => (
  <WordCloudSvg
    viewBox={`0 0 ${WC_VIEW_W} ${WC_VIEW_H}`}
    preserveAspectRatio="xMidYMid slice"
    role="img"
    aria-label="Voices in a classroom: a cloud of words students might say"
  >
    {WORD_CLOUD.map((wd, i) => {
      const cx = (wd.x / 100) * WC_VIEW_W;
      const cy = (wd.y / 100) * WC_VIEW_H;
      return (
        <text
          key={`${wd.text}-${i}`}
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily={WC_FAMILIES[wd.family]}
          fontWeight={wd.weight ?? 400}
          fontStyle={wd.italic ? 'italic' : 'normal'}
          fontSize={wd.size}
          fill={WC_COLORS[wd.color ?? 'ink']}
          opacity={wd.opacity ?? 1}
          transform={wd.rot ? `rotate(${wd.rot} ${cx} ${cy})` : undefined}
          style={{ animationDelay: `${(i % 14) * 70}ms` }}
        >
          {wd.text}
        </text>
      );
    })}
  </WordCloudSvg>
);

const ReportingInsightCard = styled.aside`
  background: #fffdf8;
  border: 1px solid #e1d6bf;
  border-radius: 14px;
  padding: 28px 30px 26px;
  box-shadow: 0 1px 0 rgba(32, 26, 46, 0.02), 0 24px 60px -28px rgba(32, 26, 46, 0.18);
  width: 90%;
  max-width: 520px;

  @media ${MOBILE_QUERY} {
    width: 100%;
    padding: 22px 22px 20px;
  }

  @media (max-width: 600px) {
    padding: 18px 18px 16px;
    border-radius: 12px;
  }
`;

const ReportingInsightLabel = styled.div`
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 700;
  color: #6d6478;
  margin-bottom: 10px;
`;

const ReportingInsightQuote = styled.p`
  font-family: ${CURRICULUM_SERIF};
  font-weight: 400;
  font-size: 26px;
  line-height: 1.22;
  letter-spacing: -0.01em;
  color: #201a2e;
  margin: 0;

  em {
    font-style: italic;
    color: #e77e1f;
    font-weight: 400;
  }

  @media ${MOBILE_QUERY} {
    font-size: 22px;
  }

  @media (max-width: 600px) {
    font-size: 18px;
    line-height: 1.28;
  }
`;

const ReportingInsightDivider = styled.div`
  height: 1px;
  background: #e1d6bf;
  margin: 22px 0 20px;

  @media (max-width: 600px) {
    margin: 16px 0 14px;
  }
`;

const ReportingNeedsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 600px) {
    gap: 12px;
  }
`;

const ReportingNeedsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  @media (max-width: 600px) {
    gap: 10px;
  }
`;

const ReportingNeedsCount = styled.div`
  font-family: ${CURRICULUM_SERIF};
  font-size: 44px;
  line-height: 1;
  font-weight: 400;
  color: #201a2e;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 34px;
  }
`;

const ReportingNeedsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .lbl {
    font-family: 'Nunito', system-ui, sans-serif;
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    font-weight: 700;
    color: #6d6478;
  }

  .sub {
    font-family: ${CURRICULUM_SERIF};
    font-style: italic;
    font-size: 16px;
    color: #3b3348;
  }

  @media (max-width: 600px) {
    .sub {
      font-size: 14px;
    }
  }
`;

const ReportingAvatars = styled.div`
  display: flex;
  align-items: center;

  .av {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #f3ebda;
    border: 2px solid #fffdf8;
    margin-left: -8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Nunito', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: #3b3348;
    letter-spacing: 0.02em;
  }

  @media (max-width: 600px) {
    .av {
      width: 30px;
      height: 30px;
      font-size: 11px;
      margin-left: -6px;
    }
  }
  .av:first-child {
    margin-left: 0;
  }
  .av.a1 {
    background: #f5dcc1;
    color: #8a4a12;
  }
  .av.a2 {
    background: #d6ece6;
    color: #0e6655;
  }
  .av.a3 {
    background: #e6ddf0;
    color: #4a3a5f;
  }
`;

const ReportingHeadline = styled.h3`
  font-weight: bold;
  font-size: 48px;
  line-height: 1.04;
  letter-spacing: -0.02em;
  color: #201a2e;
  margin: 0;
  text-wrap: pretty;
  max-width: 17ch;

  em {
    font-style: italic;
    font-weight: bold;
    color: #e77e1f;
  }

  @media ${MOBILE_QUERY} {
    font-size: 32px;
  }
`;

const ReportingLede = styled.p`
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 18px;
  line-height: 1.6;
  color: #3b3348;
  font-weight: bold;
  max-width: 50ch;
  margin: 0;

  strong {
    color: #201a2e;
    font-weight: 700;
  }

  @media ${MOBILE_QUERY} {
    font-size: 16px;
  }
`;

const ValuePropCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 520px;
`;

const ValuePropTitle = styled.h3`
  font-size: 48px;
  font-weight: bold;
  line-height: 1.05;
  letter-spacing: -0.02em;
  margin: 0;
  color: ${theme.colors.black};

  @media ${MOBILE_QUERY} {
    font-size: 32px;
  }
`;

const ValuePropBody = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin: 0;
  color: ${theme.colors.text.dark};
  @media ${MOBILE_QUERY} {
    font-size: 16px;
  }
`;

const ValuePropSource = styled.p`
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  font-style: italic;
  color: rgba(0, 0, 0, 0.55);
`;

const ValuePropLink = styled.a`
  align-self: flex-start;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 0.02em;
  text-decoration: none;
  cursor: pointer;
  color: ${theme.colors.black};
  border: none;
  background: none;
  padding: 0 0 2px 0;
  border-bottom: 2px solid ${theme.colors.orange};
  font-family: inherit;

  &:hover {
    color: ${theme.colors.orange};
  }
`;

// ============================================================
// HQIMAlignmentSection — Curriculum Alignment (editorial layout)
// ============================================================

const HQIM_CURRICULA_VISIBLE: string[] = [
  'Bluebonnet Learning',
  'CKLA Amplify',
  'Wonders',
  'HMH Into Reading',
  'EL Education',
  'Wit & Wisdom',
];

const HQIM_CURRICULA_EXTRA: string[] = [
  'StudySync',
  'myView Literacy',
  'Benchmark Advance',
  'ARC Core',
  'Into Literature',
  'SAVVAS myPerspectives',
  'Collections',
  'ReadyGEN',
  'SpringBoard ELA',
];

const HQIMAlignmentSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <CurriculumSection id="hqim-alignment">
      <CurriculumGrid>
        <CurriculumCopy>
          <CurriculumHeadline>
            <T
              keyName="LandingPage.HQIM.HeadlineLead.v2"
              defaultValue="Aligned with 55+ top-rated curricula"
            />
          </CurriculumHeadline>

          <CurriculumLede>
            <T
              keyName="LandingPage.HQIM.SubtitleV3"
              defaultValue="Curipod maps to the curricula your schools already trust, and we partner directly with districts to build lesson sets that fit their scope, sequence, and students. "
            />
            <CurriculumInlineLink href="/curriculum/bluebonnet">
              <T
                keyName="LandingPage.HQIM.InlineLink"
                defaultValue="Read how Dickinson ISD uses Curipod with Bluebonnet"
              />
              <span className="arrow" aria-hidden>
                →
              </span>
            </CurriculumInlineLink>
          </CurriculumLede>
        </CurriculumCopy>

        <CurriculumCard aria-label="Curricula Curipod aligns with">
          <CurriculumCardHead>
            <T
              keyName="LandingPage.HQIM.CardTitle"
              defaultValue="Aligns with the curricula you already use"
            />
          </CurriculumCardHead>

          <CurriculumList>
            {HQIM_CURRICULA_VISIBLE.map(name => (
              <li key={name}>{name}</li>
            ))}
          </CurriculumList>

          <CurriculumExtra $open={isExpanded} aria-hidden={!isExpanded}>
            <div className="extra-inner">
              <CurriculumList>
                {HQIM_CURRICULA_EXTRA.map(name => (
                  <li key={name}>{name}</li>
                ))}
                <li className="more">
                  <T keyName="LandingPage.HQIM.CardMore" defaultValue="and 40+ more" />
                </li>
              </CurriculumList>
            </div>
          </CurriculumExtra>

          <CurriculumToggle
            type="button"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded(v => !v)}
          >
            {isExpanded ? (
              <T keyName="LandingPage.HQIM.ShowFewer" defaultValue="Show fewer" />
            ) : (
              <T keyName="LandingPage.HQIM.ShowMore" defaultValue="Show more" />
            )}
            <span className={`chev ${isExpanded ? 'open' : ''}`} aria-hidden />
          </CurriculumToggle>
        </CurriculumCard>
      </CurriculumGrid>
    </CurriculumSection>
  );
};

const CurriculumSection = styled.section`
  position: relative;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 140px 80px;
  color: #201a2e;

  &::before {
    content: '';
    position: absolute;
    left: 80px;
    right: 80px;
    top: 0;
    height: 1px;
    background: #e1d6bf;
  }

  @media (max-width: 1100px) {
    padding: 100px 48px;

    &::before {
      left: 48px;
      right: 48px;
    }
  }

  @media (max-width: 640px) {
    padding: 72px 24px;

    &::before {
      left: 24px;
      right: 24px;
    }
  }
`;

const CurriculumGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 100px;
  align-items: center;
  justify-items: center;

  @media ${MOBILE_QUERY} {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
    justify-items: stretch;
  }
`;

const CurriculumCopy = styled.div`
  display: flex;
  flex-direction: column;

  @media ${MOBILE_QUERY} {
    order: -1;
    width: 100%;
  }
`;

const CurriculumEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 12px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: bold;
  color: #e77e1f;
  margin-bottom: 36px;
`;

const CurriculumHeadline = styled.h2`
  font-weight: bold;
  font-size: 38px;
  line-height: 1.06;
  letter-spacing: -0.02em;
  color: #201a2e;
  margin: 0 0 32px 0;
  text-wrap: pretty;
  max-width: 18ch;

  em {
    font-style: italic;
    font-weight: bold;
    color: #e77e1f;
  }

  .soft {
    color: #6d6478;
    font-weight: 300;
  }

  @media (max-width: 1100px) {
    font-size: 32px;
  }

  @media (max-width: 640px) {
    font-size: 32px;
  }
`;

const CurriculumLede = styled.p`
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 18px;
  line-height: 1.6;
  color: #3b3348;
  max-width: 48ch;
  margin: 0;
  font-weight: bold;
  @media ${MOBILE_QUERY} {
    font-size: 16px;
  }
`;

const CurriculumInlineLink = styled.a`
  color: #e77e1f;
  font-weight: bold;
  text-decoration: none;
  border-bottom: 1px solid rgba(231, 126, 31, 0.35);
  padding-bottom: 1px;
  display: inline;
  transition: border-color 180ms ease;
  white-space: normal;

  &:hover {
    border-bottom-color: #e77e1f;
  }

  .arrow {
    display: inline-block;
    margin-left: 6px;
    transition: transform 180ms ease;
  }

  &:hover .arrow {
    transform: translateX(3px);
  }
`;

const CurriculumCard = styled.aside`
  background: #fffdf9;
  border: 1px solid rgba(231, 126, 31, 0.35);
  border-radius: 22px;
  padding: 26px 28px;
  position: relative;
  box-shadow: 0 1px 0 rgba(32, 26, 46, 0.02), 0 30px 60px -40px rgba(32, 26, 46, 0.18);
  width: 100%;
  max-width: 460px;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 640px) {
    padding: 28px 20px;
  }

  @media (max-width: 360px) {
    padding: 24px 16px;
  }
`;

const CurriculumCardHead = styled.div`
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: bold;
  color: #000000;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(231, 126, 31, 0.25);
  overflow-wrap: anywhere;
  hyphens: auto;

  @media (max-width: 360px) {
    letter-spacing: 0.12em;
  }
`;

const CurriculumList = styled.ul`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 20px;
  font-family: ${CURRICULUM_SERIF};
  font-size: 14px;
  color: #3b3348;
  font-weight: bold;
  margin: 0;
  padding: 0;

  li {
    list-style: none;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0;
    line-height: 1.3;
  }

  li::before {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #f7993d;
    flex-shrink: 0;
  }

  .more {
    color: #e77e1f;
    font-style: italic;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    font-size: 16px;
  }
`;

const CurriculumExtra = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  transition: grid-template-rows 380ms cubic-bezier(0.4, 0, 0.2, 1);

  > .extra-inner {
    overflow: hidden;
    min-height: 0;
  }

  ${CurriculumList} {
    margin-top: 14px;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(-4px)')};
    transition: opacity 280ms ease 60ms, transform 280ms ease 60ms;
  }
`;

const CurriculumToggle = styled.button`
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: none;
  padding: 14px 0 0;
  margin-top: 16px;
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.02em;
  color: #e77e1f;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  transition: opacity 180ms ease;

  &:hover {
    opacity: 0.75;
  }

  .chev {
    width: 10px;
    height: 10px;
    display: inline-block;
    border-right: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    transform: rotate(45deg) translate(-1px, -1px);
    transition: transform 280ms ease;
  }

  .chev.open {
    transform: rotate(-135deg) translate(-1px, -1px);
  }
`;

// ============================================================
// EvaluationCaseStudySection — locale-branched (T-TESS for TX, Danielson elsewhere)
// ============================================================

const EvaluationCaseStudySection = () => {
  // const tolgee = useTolgee(['language']);
  // const isTexas = tolgee.getLanguage() === 'en-US-TX';
  // return isTexas ? <TtessTiaCaseStudy /> : <DanielsonCaseStudy />;
  return <DanielsonCaseStudy />;
};

export const TtessTiaCaseStudy = () => (
  <CurriculumSection aria-labelledby="ttess-headline">
    <CurriculumGrid>
      <TtessCase>
        <TtessCaseLabel>
          <span className="dot" aria-hidden />
          <T
            keyName="LandingPage.TtessTia.CaseLabel"
            defaultValue="Teacher Story · Recognized → Exemplary"
          />
        </TtessCaseLabel>
        <TtessCaseTitle>
          <T
            keyName="LandingPage.TtessTia.CaseTitle"
            defaultValue="How Ms. Reyes earned her TIA designation"
          />
        </TtessCaseTitle>
        <TtessByline>
          <T
            keyName="LandingPage.TtessTia.Byline"
            defaultValue="Daniela Reyes · 7th Grade ELA · Pharr-San Juan-Alamo ISD"
          />
        </TtessByline>
        <TtessQuote>
          <T
            keyName="LandingPage.TtessTia.Quote"
            defaultValue="“My appraiser kept marking Monitor & Adjust as ‘Developing.’ With Curipod I finally had live evidence — every student answering, every lesson — and my scores jumped two levels in one cycle.”"
          />
        </TtessQuote>
        <TtessBody>
          <T
            keyName="LandingPage.TtessTia.Body"
            defaultValue="In her first full year using Curipod, Daniela moved from Proficient to Distinguished on three T-TESS dimensions and qualified for Exemplary under her district's TIA plan."
          />
        </TtessBody>
        <TtessPayout>
          <span className="amt">+$9,000</span>
          <span className="designation">
            <T
              keyName="LandingPage.TtessTia.PayoutLabel"
              defaultValue="Exemplary · annual TIA allotment"
            />
          </span>
        </TtessPayout>
        {/* <TtessCaseLink href="#">
          <T
            keyName="LandingPage.TtessTia.CaseLink"
            defaultValue="Read Daniela's full story"
          />
          <span className="arrow" aria-hidden>
            →
          </span>
        </TtessCaseLink> */}
      </TtessCase>

      <CurriculumCopy>
        <CurriculumEyebrow>
          <T keyName="LandingPage.TtessTia.Tag" defaultValue="T-TESS & TIA" />
        </CurriculumEyebrow>
        <CurriculumHeadline id="ttess-headline">
          <T keyName="LandingPage.TtessTia.HeadlineLead" defaultValue="Top scores on " />
          <em>
            <T keyName="LandingPage.TtessTia.HeadlineEm" defaultValue="T-TESS" />
          </em>
          ,<br />
          <span className="soft">
            <T
              keyName="LandingPage.TtessTia.HeadlineSoft"
              defaultValue="and a path to the TIA stipend."
            />
          </span>
        </CurriculumHeadline>
        <CurriculumLede>
          <T
            keyName="LandingPage.TtessTia.Subtitle"
            defaultValue="Teachers using Curipod consistently hit Accomplished and Distinguished across the T-TESS rubric — the same evidence Texas districts use to designate teachers for the Teacher Incentive Allotment."
          />
        </CurriculumLede>
      </CurriculumCopy>
    </CurriculumGrid>
  </CurriculumSection>
);

const DANIELSON_DOMAIN_3: {
  code: string;
  nameKey: string;
  nameDefault: string;
  descKey: string;
  descDefault: string;
}[] = [
  {
    code: '3b',
    nameKey: 'LandingPage.Danielson.Comp3bName',
    nameDefault: 'Engaging Students in Learning',
    descKey: 'LandingPage.Danielson.Comp3bDesc',
    descDefault: 'Active intellectual work, not passive listening.',
  },
  {
    code: '3c',
    nameKey: 'LandingPage.Danielson.Comp3cName',
    nameDefault: 'Communicating About Purpose & Content',
    descKey: 'LandingPage.Danielson.Comp3cDesc',
    descDefault: 'Clear learning intentions, every slide.',
  },
  {
    code: '3d',
    nameKey: 'LandingPage.Danielson.Comp3dName',
    nameDefault: 'Using Assessment for Learning',
    descKey: 'LandingPage.Danielson.Comp3dDesc',
    descDefault: 'Real-time evidence; teachers adjust on the fly.',
  },
  {
    code: '3e',
    nameKey: 'LandingPage.Danielson.Comp3eName',
    nameDefault: 'Responsiveness & Flexibility',
    descKey: 'LandingPage.Danielson.Comp3eDesc',
    descDefault: 'Pivots based on what students actually show.',
  },
];

const DanielsonCaseStudy = () => (
  <CurriculumSection aria-labelledby="danielson-headline">
    <CurriculumGrid>
      <DanielsonCard aria-label="Danielson Framework components">
        <span className="corner tl" aria-hidden />
        <span className="corner tr" aria-hidden />
        <span className="corner bl" aria-hidden />
        <span className="corner br" aria-hidden />

        <DanielsonHead>
          <span>
            <T
              keyName="LandingPage.Danielson.CardEyebrow"
              defaultValue="What evaluators score"
            />
          </span>
          <em>
            <T
              keyName="LandingPage.Danielson.CardSubeyebrow"
              defaultValue="Danielson FFT · 2022 edition"
            />
          </em>
        </DanielsonHead>

        <DanielsonDomainTag $variant="teal">
          <span className="num">2</span>
          <T
            keyName="LandingPage.Danielson.Domain2"
            defaultValue="Domain 2 · Learning Environment"
          />
        </DanielsonDomainTag>
        <DanielsonCompRow>
          <span className="code">2b</span>
          <div>
            <div className="name">
              <T
                keyName="LandingPage.Danielson.Comp2bName"
                defaultValue="Fostering a Culture for Learning"
              />
            </div>
            <div className="desc">
              <T
                keyName="LandingPage.Danielson.Comp2bDesc"
                defaultValue="Every student responds — not just a few hands."
              />
            </div>
          </div>
        </DanielsonCompRow>

        <DanielsonDomainTag $variant="orange">
          <span className="num">3</span>
          <T
            keyName="LandingPage.Danielson.Domain3"
            defaultValue="Domain 3 · Learning Experiences"
          />
        </DanielsonDomainTag>
        {DANIELSON_DOMAIN_3.map(c => (
          <DanielsonCompRow key={c.code}>
            <span className="code">{c.code}</span>
            <div>
              <div className="name">
                <T keyName={c.nameKey} defaultValue={c.nameDefault} />
              </div>
              <div className="desc">
                <T keyName={c.descKey} defaultValue={c.descDefault} />
              </div>
            </div>
          </DanielsonCompRow>
        ))}
      </DanielsonCard>

      <CurriculumCopy>
        {/* <CurriculumEyebrow>
          <T
            keyName="LandingPage.Danielson.Tag"
            defaultValue="Observation & Evaluation"
          />
        </CurriculumEyebrow> */}
        <CurriculumHeadline id="danielson-headline">
          <T
            keyName="LandingPage.Danielson.HeadlineLead.2"
            defaultValue="Top scores across Danielson, Marzano, and state evaluation frameworks"
          />
        </CurriculumHeadline>
        <CurriculumLede>
          <T
            keyName="LandingPage.Danielson.Subtitle"
            defaultValue="Curipod produces the live, every-student evidence that observers look for under Danielson, Marzano, T-TESS, CSTP, and most state rubrics — without changing how teachers teach."
          />
        </CurriculumLede>
      </CurriculumCopy>
    </CurriculumGrid>
  </CurriculumSection>
);

const TtessCase = styled.div`
  background: #fffdf8;
  border: 1px solid #e1d6bf;
  border-radius: 6px;
  padding: 24px 26px 22px;
  position: relative;
  box-shadow: 0 1px 0 rgba(32, 26, 46, 0.02), 0 20px 40px -30px rgba(32, 26, 46, 0.15);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 24px;
    bottom: 24px;
    width: 3px;
    background: #f7993d;
    border-radius: 0 2px 2px 0;
  }
`;

const TtessCaseLabel = styled.div`
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: bold;
  color: #e77e1f;
  margin-bottom: 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f7993d;
  }
`;

const TtessCaseTitle = styled.h3`
  font-family: ${CURRICULUM_SERIF};
  font-weight: bold;
  font-size: 19px;
  letter-spacing: -0.01em;
  color: #201a2e;
  line-height: 1.3;
  margin: 0 0 10px;
`;

const TtessByline = styled.p`
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: bold;
  color: #6d6478;
  margin: 0 0 16px;
`;

const TtessQuote = styled.blockquote`
  font-family: ${CURRICULUM_SERIF};
  font-style: italic;
  font-size: 16px;
  line-height: 1.5;
  color: #201a2e;
  margin: 0 0 14px;
  font-weight: 400;
`;

const TtessBody = styled.p`
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 14.5px;
  line-height: 1.6;
  color: #3b3348;
  margin: 0 0 14px;
`;

const TtessPayout = styled.div`
  margin-top: 6px;
  padding: 12px 14px;
  background: #fce4ce;
  border-radius: 4px;
  display: flex;
  align-items: baseline;
  gap: 14px;

  .amt {
    font-family: ${CURRICULUM_SERIF};
    font-size: 22px;
    font-weight: 600;
    color: #e77e1f;
    letter-spacing: -0.01em;
  }

  .designation {
    font-family: 'Nunito', system-ui, sans-serif;
    font-size: 11px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 700;
    color: #3b3348;
  }
`;

const DanielsonCard = styled.div`
  background: #fffdf8;
  border: 1px solid #e1d6bf;
  border-radius: 6px;
  padding: 36px 40px 32px;
  position: relative;
  box-shadow: 0 1px 0 rgba(32, 26, 46, 0.02), 0 30px 60px -40px rgba(32, 26, 46, 0.18);

  .corner {
    position: absolute;
    width: 14px;
    height: 14px;
    border: 1px solid #e1d6bf;
  }
  .corner.tl {
    top: 12px;
    left: 12px;
    border-right: none;
    border-bottom: none;
  }
  .corner.tr {
    top: 12px;
    right: 12px;
    border-left: none;
    border-bottom: none;
  }
  .corner.bl {
    bottom: 12px;
    left: 12px;
    border-right: none;
    border-top: none;
  }
  .corner.br {
    bottom: 12px;
    right: 12px;
    border-left: none;
    border-top: none;
  }

  @media (max-width: 640px) {
    padding: 28px 24px 24px;
  }
`;

const DanielsonHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e1d6bf;

  > span {
    font-family: 'Nunito', system-ui, sans-serif;
    font-size: 12px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    font-weight: 700;
    color: #6d6478;
  }

  > em {
    font-family: ${CURRICULUM_SERIF};
    font-style: italic;
    font-size: 12px;
    color: #6d6478;
    text-align: right;
  }
`;

const DanielsonDomainTag = styled.div<{ $variant: 'teal' | 'orange' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 6px;
  border-radius: 999px;
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 10.5px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 800;
  margin: 18px 0 4px;

  &:first-of-type {
    margin-top: 4px;
  }

  ${({ $variant }) =>
    $variant === 'teal'
      ? css`
          background: #d6ece6;
          color: #0e7d6a;
        `
      : css`
          background: #fce4ce;
          color: #e77e1f;
        `}

  .num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.75);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: ${CURRICULUM_SERIF};
    font-style: italic;
    font-weight: 600;
    font-size: 13px;
    flex-shrink: 0;
  }
`;

const DanielsonCompRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: baseline;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px dashed #ebe2cf;

  &:last-of-type {
    border-bottom: none;
  }

  .code {
    font-family: ${CURRICULUM_SERIF};
    font-style: italic;
    font-size: 17px;
    font-weight: 500;
    color: #e77e1f;
    min-width: 26px;
  }

  .name {
    font-family: ${CURRICULUM_SERIF};
    font-size: 16px;
    font-weight: 600;
    color: #201a2e;
    line-height: 1.25;
  }

  .desc {
    font-family: 'Nunito', system-ui, sans-serif;
    font-size: 12.5px;
    line-height: 1.55;
    color: #6d6478;
    font-weight: 500;
    margin-top: 2px;
  }
`;

// ============================================================
// WallOfLoveSection — testimonial.to iframe embed
// ============================================================

const WallOfLoveSection = () => {
  const isDesktop = useBreakPoint(768);
  return (
    <WallOfLoveSectionWrapper>
      <LandingPageContent>
        <CenteredSectionInner>
          {/* <SectionTag>
            <T keyName="LandingPage.WallOfLove.TagV2" defaultValue="WALL OF LOVE" />
          </SectionTag> */}
          <SectionTitle>
            <T
              keyName="LandingPage.WallOfLove.HeadingV2"
              defaultValue="Why teachers love Curipod"
            />
          </SectionTitle>
        </CenteredSectionInner>
      </LandingPageContent>
      {isDesktop && (
        <Script
          src="https://testimonial.to/js/iframeResizer.min.js"
          strategy="lazyOnload"
          onLoad={() => {
            const resize = ((window as unknown) as {
              iFrameResize?: (...args: unknown[]) => void;
            }).iFrameResize;
            if (typeof resize === 'function') {
              resize(
                { log: false, checkOrigin: false },
                '#testimonialto-7034ee94-fe10-4fc8-92f1-bbd3bc3ba108',
              );
            }
          }}
        />
      )}
      {!isDesktop && (
        <Script
          src="https://testimonial.to/js/iframeResizer.min.js"
          strategy="lazyOnload"
          onLoad={() => {
            const resize = ((window as unknown) as {
              iFrameResize?: (...args: unknown[]) => void;
            }).iFrameResize;
            if (typeof resize === 'function') {
              resize(
                { log: false, checkOrigin: false },
                '#testimonialto-ffc44b01-2a13-41b4-b662-edb6c33b0712',
              );
            }
          }}
        />
      )}
      <WallOfLoveFrameDesktop
        id="testimonialto-7034ee94-fe10-4fc8-92f1-bbd3bc3ba108"
        src="https://embed-v2.testimonial.to/w/curipod?id=7034ee94-fe10-4fc8-92f1-bbd3bc3ba108&theme=light&card=base"
        frameBorder="0"
        scrolling="no"
        width="100%"
        title="Wall of Love"
        loading="lazy"
      />

      {/* <!-- Testimonial.to embed for Mobile -->
      
        <script type="text/javascript" src="https://testimonial.to/js/iframeResizer.min.js"></script>
<iframe id='testimonialto-ffc44b01-2a13-41b4-b662-edb6c33b0712' src="https://embed-v2.testimonial.to/carousel/all/curipod?id=ffc44b01-2a13-41b4-b662-edb6c33b0712" frameborder="0" scrolling="no" width="100%" ></iframe>
<script type="text/javascript">iFrameResize({log: false, checkOrigin: false}, '#testimonialto-ffc44b01-2a13-41b4-b662-edb6c33b0712');</script>
      
      */}
      <WallOfLoveFrameMobile
        id="testimonialto-ffc44b01-2a13-41b4-b662-edb6c33b0712"
        src="https://embed-v2.testimonial.to/carousel/all/curipod?id=ffc44b01-2a13-41b4-b662-edb6c33b0712"
        frameBorder="0"
        scrolling="no"
        width="100%"
        title="Wall of Love Mobile"
        loading="lazy"
      />
    </WallOfLoveSectionWrapper>
  );
};

const WallOfLoveSectionWrapper = styled.section`
  position: relative;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  /* background: ${theme.colors.orange}; */
  padding: 100px 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${MOBILE_QUERY} {
    padding: 70px 0 10px;
  }
`;

const WallOfLoveFrame = styled.iframe`
  width: 100%;
  max-width: 1301px;
  min-height: 1500px;
  border: 0;
  margin: 32px auto 0;
  display: block;
  @media ${MOBILE_QUERY} {
    min-height: 600px;
  }
`;

const WallOfLoveFrameDesktop = styled(WallOfLoveFrame)`
  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

const WallOfLoveFrameMobile = styled(WallOfLoveFrame)`
  display: none;
  @media ${MOBILE_QUERY} {
    display: block;
  }
`;

// ============================================================
// PrivacySection — full-height trust moment
// ============================================================

type PrivacyBadge = {
  label: string;
  hint: string;
  image: { src: string } | string;
};

type PrivacyGroup = {
  titleKey: string;
  titleDefault: string;
  items: PrivacyBadge[];
  large?: boolean;
};

const PRIVACY_GROUPS: PrivacyGroup[] = [
  {
    titleKey: 'LandingPage.Privacy.GroupCompliance',
    titleDefault: 'Compliance',
    large: true,
    items: [
      {
        label: 'FERPA',
        hint: 'Family Educational Rights and Privacy Act — aligned',
        image: ferpaBadge,
      },
      {
        label: 'COPPA',
        hint: "Children's Online Privacy Protection Act — compliant",
        image: coppaBadge,
      },
      {
        label: 'GDPR',
        hint: 'EU General Data Protection Regulation — compliant',
        image: gdprBadge,
      },
    ],
  },
  {
    titleKey: 'LandingPage.Privacy.GroupIntegrations',
    titleDefault: 'Integrations',
    items: [
      { label: 'ClassLink', hint: 'ClassLink SSO', image: classlinkLogo },
      { label: 'Clever', hint: 'Clever SSO', image: cleverLogo },
      {
        label: 'Google for Education',
        hint: 'Google for Education SSO',
        image: googleForEducationLogo,
      },
    ],
  },
  {
    titleKey: 'LandingPage.Privacy.GroupReview',
    titleDefault: 'Independent review',
    items: [
      {
        label: 'Common Sense Media',
        hint: 'Common Sense Media — trusted reviewer',
        image: commonSenseMedia,
      },
    ],
  },
];

const PrivacySection = () => {
  return (
    <PrivacyDarkSection>
      <LandingPageContent>
        <PrivacySplit>
          <PrivacyLeftCol>
            {/* <PrivacyTag>
              <T keyName="LandingPage.Privacy.Tag" defaultValue="TRUST & SAFETY" />
            </PrivacyTag> */}
            <PrivacyH2>
              <T
                keyName="LandingPage.Privacy.Heading"
                defaultValue="Privacy & Security by Design"
              />
            </PrivacyH2>
            <PrivacyLead>
              <T
                keyName="LandingPage.Privacy.Subtitle"
                defaultValue="Curipod is built for schools — FERPA, COPPA, and GDPR compliant. Students do not have accounts. No AI Chatbots. 100% teacher controlled."
              />
            </PrivacyLead>
            <PrivacyLead>
              <T
                keyName="LandingPage.Privacy.SSONote"
                defaultValue="Works with ClassLink, Clever and Google Single Sign-On so rollout is painless for IT."
              />
            </PrivacyLead>
            <PrivacyFooterLink href="/privacy-policy">
              <T
                keyName="LandingPage.Privacy.PolicyLink"
                defaultValue="Read our full privacy & security commitments →"
              />
            </PrivacyFooterLink>
          </PrivacyLeftCol>

          <PrivacyRightCol>
            <PrivacyStack>
              {PRIVACY_GROUPS.map(group => (
                <PrivacyStackGroup key={group.titleKey}>
                  <PrivacyStackTitle>
                    <T keyName={group.titleKey} defaultValue={group.titleDefault} />
                  </PrivacyStackTitle>
                  <PrivacyStackRow $cols={group.items.length}>
                    {group.items.map(badge => (
                      <PrivacyStackCard
                        key={badge.label}
                        aria-label={badge.hint}
                        title={badge.hint}
                      >
                        <PrivacyStackImage
                          $large={group.large}
                          src={
                            typeof badge.image === 'string'
                              ? badge.image
                              : badge.image.src
                          }
                          alt={badge.label}
                        />
                      </PrivacyStackCard>
                    ))}
                  </PrivacyStackRow>
                </PrivacyStackGroup>
              ))}
            </PrivacyStack>
          </PrivacyRightCol>
        </PrivacySplit>
      </LandingPageContent>
    </PrivacyDarkSection>
  );
};

const PrivacyDarkSection = styled.section`
  position: relative;
  width: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 100px 0;
  overflow: hidden;
  display: flex;
  justify-content: center;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE_QUERY} {
    padding: 60px 0;
  }
`;

const PrivacySplit = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  margin-bottom: 48px;
  position: relative;
  z-index: 1;

  @media ${MOBILE_QUERY} {
    grid-template-columns: 1fr;
    gap: 40px;
    margin-bottom: 40px;
  }
`;

const PrivacyLeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PrivacyH2 = styled.h2`
  font-size: 38px;
  font-weight: 900;
  line-height: 1.2;
  margin: 0;
  color: ${theme.colors.white};

  @media ${MOBILE_QUERY} {
    font-size: 32px;
  }
`;

const PrivacyLead = styled.p`
  font-size: 18px;
  line-height: 1.65;
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  max-width: 520px;

  @media ${MOBILE_QUERY} {
    font-size: 15px;
  }
`;

const PrivacyRightCol = styled.div`
  display: flex;
  justify-content: stretch;
  align-items: center;
  position: relative;
  width: 100%;
`;

const PrivacyStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
`;

const PrivacyStackGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const PrivacyStackTitle = styled.div`
  color: #8b94be;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const PrivacyStackRow = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: ${({ $cols }) => `repeat(${$cols}, 1fr)`};
  gap: 10px;

  @media (max-width: 600px) {
    grid-template-columns: ${({ $cols }) =>
      $cols >= 3 ? 'repeat(2, 1fr)' : `repeat(${$cols}, 1fr)`};
  }
`;

const PrivacyStackCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 120px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
  transition: ${theme.effects.defaultTransition};
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
  }
`;

const PrivacyStackImage = styled.img<{ $large?: boolean }>`
  max-height: ${({ $large }) => ($large ? '96px' : '56px')};
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const PrivacyFooterLink = styled.a`
  margin-top: 6px;
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.orange};
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 4px;
  width: fit-content;

  &:hover {
    opacity: 0.85;
  }
`;

// ============================================================
// FinalDemoCtaSection — closing book-a-demo CTA
// ============================================================

const FinalDemoCtaSection = ({ onBookDemo }: { onBookDemo: () => void }) => (
  <FinalCtaWrapper>
    <FinalCtaTitle>
      <T
        keyName="LandingPage.FinalCta.Title"
        defaultValue="Bring Curipod to your School or District"
      />
    </FinalCtaTitle>
    <FinalCtaButton onClick={onBookDemo}>
      <T keyName="LandingPage.FinalCta.BookDemo" defaultValue="Book a Demo" />
    </FinalCtaButton>
  </FinalCtaWrapper>
);

const FinalCtaWrapper = styled.section`
  width: 100%;
  background-color: ${theme.colors.background};
  padding: 100px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;

  @media (max-width: 600px) {
    padding: 72px 24px;
    gap: 24px;
  }
`;

const FinalCtaTitle = styled.h2`
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: clamp(28px, 4.2vw, 48px);
  font-weight: 900;
  line-height: 1.15;
  text-align: center;
  color: ${theme.colors.black};
  max-width: 22ch;
  margin: 0;
`;

const FinalCtaButton = styled(OrangePrimaryButton)`
  width: auto;
  min-width: 240px;
  height: 56px;
  padding: 0 36px;
  border-radius: 10px;

  span {
    font-size: 22px;
    font-weight: 800;
  }
`;

const LandingPageHome = () => {
  const router = useRouter();
  useLocale();
  const metrics = useMetrics(true);
  const isDesktop = useBreakPoint(768);

  const handleSignupClicked = useCallback(() => {
    metrics.logEvent('FrontPage.SignupClicked', {
      from: 'tryForFree',
    });

    const trackingParams = getTrackingParams();
    const query: Record<string, string> = {
      callback: `/`,
    };

    // Fallback for TrackingParamsProvider: pass in URL when localStorage may be unavailable (e.g. private browsing)
    if (trackingParams.fbclid) {
      query.fbclid = trackingParams.fbclid;
    }
    if (trackingParams.ttclid) {
      query.ttclid = trackingParams.ttclid;
    }

    router.push({
      pathname: '/signup',
      query,
    });
  }, [metrics, router]);

  const handleBookDemo = useCallback(() => {
    metrics.logEvent('FrontPage.BookDemoClicked', {
      from: 'hero',
    });
    window.open('/quotes/', '_curipod_quote');
  }, [metrics]);

  const handleFinalCtaBookDemo = useCallback(() => {
    metrics.logEvent('FrontPage.BookDemoClicked', {
      from: 'final-cta',
    });
    window.open('/quotes/', '_curipod_quote');
  }, [metrics]);

  const handleProvenOutcomesClick = useCallback(() => {
    metrics.logEvent('FrontPage.ProvenOutcomesClicked');
    // Dispatch custom event to trigger Impact & Efficacy dropdown in header
    window.dispatchEvent(new CustomEvent('openImpactEfficacyDropdown'));
  }, [metrics]);

  return (
    <Container>
      <GetStudentsExcited
        onSignup={handleSignupClicked}
        onBookDemo={handleBookDemo}
        onProvenOutcomesClick={handleProvenOutcomesClick}
        isDesktop={isDesktop}
      />
      <TrustedByScroller />
      <CoreValuePropositionSection onBookDemo={handleBookDemo} />
      <WallOfLoveSection />
      <HQIMAlignmentSection />
      <EvaluationCaseStudySection />
      {/* <TestPrep /> */}
      <PrivacySection />
      <VideoSection />
      <FinalDemoCtaSection onBookDemo={handleFinalCtaBookDemo} />
    </Container>
  );
};

export default LandingPageHome;
